import type {BarcodeScannerService} from '../../../../barcode-scanner/barcode-scanner.service';
import type {Subscription} from 'rxjs';
import type {UserFeedbackService} from '../../../services/user-feedback.service';
import {distinctUntilChanged, take, tap} from 'rxjs/operators';
import {Barcode} from '../../../../barcode-scanner/barcode';
import type {FormControl} from '../../internal/forms/form-control';
import type {ActionReturn} from 'svelte/types/runtime/action';

interface Attributes {
	'on:barcode-scan': (e: CustomEvent<void>) => void;
}

interface Parameters {
    formControl: FormControl<unknown>;
    barcodeScannerService: BarcodeScannerService;
    userFeedbackService: UserFeedbackService;
}

export function barcodeScanner(element: HTMLElement, {formControl, barcodeScannerService, userFeedbackService}: Parameters): ActionReturn<Parameters, Attributes> {

    const focusEvent = 'focus';
    const blurEvent = 'blur';
    const INVALID_ELEMENT_MSG = 'Barcode scanner directive requires input element';

    const focusEventHandler = () => subscribeValues();
    const blurEventHandler = () => unsubscribeValues();

    let subscription: Subscription;

    if (!(element instanceof HTMLInputElement)) {
        throw new TypeError(INVALID_ELEMENT_MSG);
    }

    if (barcodeScannerService.isUsable()) {
        element.addEventListener(focusEvent, focusEventHandler);
        element.addEventListener(blurEvent, blurEventHandler);

        barcodeScannerService.registerBarcodeScannerConsumer();
    }

    function subscribeValues() {
        subscription = barcodeScannerService.getScannedBarcode$()
            .pipe(
                distinctUntilChanged(Barcode.equals),
                take(1), // take only 1 barcode, then unsubscribe
                tap(signalSuccessfulScan), // beep & vibrate
                tap(() => barcodeScannerService.stopScanning())) // stop scanning
            .subscribe(fillInputValue);
    }

    function unsubscribeValues() {
        if (subscription) {
            subscription.unsubscribe()
            subscription = null;
        }
    }

    function fillInputValue(barcode: Barcode) {
        formControl.setViewValue(barcode.text);
        element.dispatchEvent(new CustomEvent('barcode-scan'));
    }

    function signalSuccessfulScan() {
        userFeedbackService.beep(300);
        userFeedbackService.vibrate(500);
    }

    return {
        destroy() {
            unsubscribeValues();
            if (barcodeScannerService.isUsable()) {

                barcodeScannerService.unregisterBarcodeScannerConsumer();

                element.removeEventListener(focusEvent, focusEventHandler);
                element.removeEventListener(blurEvent, blurEventHandler);
            }
        }
    }
}